<template>
  <SectionContentLayout
    content_class="contacts-person-form">
    <template #close>
      <ActionButton
          @click='onActionClose()'
          v-tooltip='"contacts.actions.close"'
          class="close"
          icon="fas fa-times-circle"
        />
    </template>

    <template #header>
        <ContentTitle title="contacts.duplicate_finder.title"/>
    </template>

    <template #content>
      <div class="form">

        <fieldset>
            <legend>{{Language.say('contacts.duplicate_finder.title_last_step')}}</legend>

            <div v-for="field in fields" :key="field">
              <div v-if="Object.keys(field_lists[field]).length > 1">
                <div v-for="(contact_field, value_field, index_field) in field_lists[field]"
                    :key="contact_field"
                    :id="`ctc_${field}`"
                    :class="{invalid: multiple_fields_errors[field]}">
                  <p class="attach-button">
                    <component
                      :is="fields_component_inputs[field] || fields_component_inputs.default"
                      :input_name="field"
                      :label_text="index_field == 0 ? `contacts.form.${field}`: ''"
                      :input_value="contact_field"
                      v-model:input_value="contact_to_group[field]">
                    </component>
                    <ActionButton
                      v-if="Object.keys(field_lists[field]).length > 1"
                      icon="fas fa-trash-alt"
                      @click="removeInvalidField(field, contact_field)">
                    </ActionButton>
                  </p>
                  <p class="error"
                      v-if="(Object.keys(field_lists[field]).length - 1 == index_field) && multiple_fields_errors[field]">
                      {{ Language.say('contacts.duplicate_finder.error_multiple_names') }}
                  </p>
                </div>
              </div>
              <div v-else>
                <component
                  :is="fields_component_inputs[field] || fields_component_inputs.default"
                  :input_id="`ctc_${field}`"
                  :input_name="field"
                  :label_text="`contacts.form.${field}`"
                  :input_value="Object.keys(field_lists[field])[0] || contact_to_group[field]"
                  :input_invalids="properties_validation.invalid"
                  v-model:input_value="contact_to_group[field]"
                  @change="validateOnChange()">
                </component>
              </div>
            </div>
        </fieldset>

        <div class="submit-desktop">
            <ActionButton class="main-action" icon="fa fa-compress"
              text="contacts.duplicate_finder.button_group"
              @click="onContactsSave" />
        </div>

      </div>

      <ConfirmModal
        v-if="confirm_modal_visible"
        :title="Language.say('contacts.duplicate_finder.title_modal_submit')"
        @actionCancel='setConfirmModalVisible(false)'
        @actionConfirm="onConfirmMultipleToOne()">
        <template #modal_body>
          <h3>{{ Language.say('contacts.duplicate_finder.text_modal_submit_1', [contacts_selection.count]) }}</h3>
          <p>
            {{ Language.say('contacts.duplicate_finder.text_modal_submit_2') }}
          </p>
        </template>
      </ConfirmModal>
    </template>
  </SectionContentLayout>
</template>

<script setup>
  import {reactive, onBeforeMount, defineEmits, defineProps, ref} from 'vue'
  import ActionButton           from '@/vue/Elements/Buttons/ActionButton.vue';
  import SectionContentLayout   from '@/vue/Layouts/Sections/SectionContentLayout.vue';
  import Language               from '@/js/Shared/Language/Language';
  import InputText              from '@/vue/Elements/Inputs/InputText.vue';
  import InputTextArea          from '@/vue/Elements/Inputs/InputTextArea.vue';
  import PropertiesValidation   from '@/js/Shared/Validation/PropertiesValidation';
  import ContentTitle           from '@/vue/Elements/Content/ContentTitle.vue';
  import ContactItem            from '@/js/Contacts/Entities/ContactItem';
  import ContactsController from '@/js/Contacts/ContactsController';
  import ConfirmModal from '@/vue/Views/Webmail/Modals/ConfirmModal.vue';

  const props = defineProps({
    contacts_duplicates_list: {},
    contacts_view: {},
    contacts_selection: {}
  });

  const emit = defineEmits([
    'contactsDuplicatesSubmitClose', 'contactsDuplicatesSubmit'
  ]);

  const contacts_controller   = ContactsController;
  const contact_to_group      = ref({});
  const confirm_modal_visible = ref(false);
  const first_submit          = ref(true);
  const multiple_name_errors  = ref(false);
  const field_correct         = ref({});
  const properties_validation = reactive(new PropertiesValidation({
    name:  {
      required: true,
    },
    email:  {
      regex: 'email',
    },
    mobile_phone: {
      regex: 'phone',
    },
    birth_date:  {
      regex: 'birth_date',
    },
  }));
  const fields = ['name', 'email', 'mobile_phone', 'company', 'home_address', 'birth_date', 'notes']
  const field_lists = reactive({});
  const multiple_fields_errors = reactive({});
  const fields_component_inputs = {
    'notes'  : InputTextArea,
    'default': InputText
  }

  onBeforeMount(async () => {
    await setMapFields();
    await setFieldLists();
    setContactEdit();
  });

  const onActionClose = () => {
    emit('contactsDuplicatesSubmitClose', props.contacts_duplicates_list.email_selected);
  }

  const setMapFields = async () => {
    fields.forEach(field => {
      multiple_fields_errors[field] = ref(false)
    })

    fields.forEach(field => {
      field_lists[field] = ref({})
    })
  }

  const setFieldLists = async () => {
    const items = props.contacts_selection.items;

    items.forEach(contact => {
      fields.forEach(field => {
        if(contact[field]){
          field_lists[field][contact[field]] = contact[field];
        }
      })
    })
  }

  const setContactEdit = () => {
    contact_to_group.value = new ContactItem();
    contact_to_group.value.email = props.contacts_duplicates_list.email_selected;
  }

  const onContactsSave = async () => {
    setFirstSubmit(false);
    setCorrectFieldValue()

    if(validateMultipleFields() === true){
      scrollToRef('name');
      return;
    }

    if (validateFields() === false) {
      scrollToRef(properties_validation.getFirstInvalid());
      return;
    }

    setConfirmModalVisible(true);
  }

  const onConfirmMultipleToOne = async () => {
    const email_selected = props.contacts_duplicates_list.email_selected;
    const contacts_to_delete = props.contacts_duplicates_list.duplicates[email_selected].getIndexKeys();

    if (await contacts_controller.savePerson(contact_to_group.value) === false) {
      return;
    }

    if (await contacts_controller.deleteContacts(contacts_to_delete, 'contacts-deleted') === false) {
      return;
    }

    props.contacts_duplicates_list.removeEmailFromDuplicates(props.contacts_duplicates_list.email_selected);
    emit('contactsDuplicatesSubmit');
  }

  const setCorrectFieldValue = () => {
    fields.forEach(field => {
      if(!contact_to_group.value[field]){
        contact_to_group.value[field] = field_correct[field] || Object.keys(field_lists[field])[0];
      }
    })
  }

  const setFirstSubmit = (value) => {
    first_submit.value = value;
  }

  const validateMultipleFields = () => {
    let hasMultipleEntries = false;

    for (let field of fields) {
      if (Object.keys(field_lists[field]).length > 1) {
        multiple_fields_errors[field] = true;
        hasMultipleEntries = true;
      } else {
        multiple_fields_errors[field] = false;
      }
    }

    return hasMultipleEntries;
  }

  const validateFields = () => {
    multiple_name_errors.value = false;
    return properties_validation.validateObject(contact_to_group.value);
  }

  const validateOnChange = () => {
    if (first_submit.value === true) {
      return;
    }

    if (validateFields() === false) {
      return;
    }
  }

  const scrollToRef = (id) => {
    document.getElementById(`ctc_${id}`).scrollIntoView({block: "end", behavior: "smooth"});
  }

  const removeInvalidField = (field, contact_field) => {
    if(first_submit.value === false){
      multiple_name_errors.value = false;
    }

    delete field_lists[field][contact_field];
    if(!field_correct.value[field] || field_correct.value[field] === contact_field) {
      contact_to_group.value[field] = Object.keys(field_lists[field])[0] || '';
    }
  }

  const setConfirmModalVisible = (value) => {
    confirm_modal_visible.value = value;
  }
</script>